import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import Carregando from './Carregando';
import { usePodeMudarEndereco } from '../lib/podeMudarEndereco';
import { plantaDaLoja } from '../lib/consultas';
import { descreverEndereco } from '../lib/enderecos';
import { daMarca, ganchosDaArea, ondeFicaAArea, type AreaDaMarca } from '../lib/marcasDasCalotas';
import { qtd } from '../lib/formato';
import { ordemDeCaminhada, type LugarDaPeca } from '../lib/ondeFica';
import { zonaDoLocal } from '../lib/zonas';

// "ONDE FICA" (desenho escolhido em 06/10/2026). Ver o lugar de uma peça custa
// UM toque e não tira ninguém da tela: a folha abre POR CIMA da Venda, da
// Busca, do catálogo e da ficha, como a foto em tela cheia (GaleriaCheia). O
// carrinho, a reserva, o texto digitado e a posição da lista ficam intactos.
// Antes, ver o lugar na Venda era sair dela, redigitar a peça no mapa e voltar.
//
// Para o vendedor, é tudo LEITURA. Botão que muda endereço ("Mudar de lugar",
// "Pôr num lugar" — fase 3, regra 3) só aparece para dono e gerente
// (usePodeMudarEndereco), e as ações vêm de useAcoesDeEndereco() lá dentro do
// fluxo, que também não existem para o vendedor.

// O desenho só desce quando a folha abre (lazy): Planta3D, cena3d e
// axonometria não entram no pacote da Venda, que o balcão carrega no 4G.
const MiniMapa = lazy(() => import('./MiniMapa'));
// O "Mudar de lugar" também: só dono e gerente o abrem, e só quando tocam.
const MudarNaFolha = lazy(() => import('./MudarDeLugar').then((m) => ({ default: m.MudarNaFolha })));

/**
 * O botão de ação que a tela põe na folha: na Venda, "Pôr no carrinho" (para
 * quem tocou na faixa achando que era o cartão); na Busca, "Abrir a ficha".
 */
export interface AcaoNoOndeFica<P> {
  rotulo: string;
  fazer: (peca: P) => void;
  /** A ação já está feita (a peça já está no carrinho): o recado no lugar do botão. */
  jaFeito?: (productId: string) => string | null;
  /** O que acontece com a tela de origem se a pessoa for ao mapa grande. */
  notaDoMapa?: string;
}

/** A peça como a folha mostra: nome, códigos, quanto há — nunca valor vendido. */
export interface PecaNaFolha {
  product_id: string;
  descricao: string;
  sku: string;
  marca?: string | null;
  /** Quanto há em estoque; null quando a tela não sabe. */
  quantidade?: number | null;
  lugares: LugarDaPeca[];
  /** Calota (15/10/2026): a área da marca. Quando vem, É o lugar — o gancho gravado não aparece. */
  area?: AreaDaMarca | null;
}

/** A ação já amarrada à peça aberta. */
export interface AcaoPronta {
  rotulo: string;
  fazer: () => void;
  jaFeito?: string | null;
  notaDoMapa?: string;
}

/** Amarra a ação de uma tela à peça que abriu a folha. */
export function acaoPara<P extends { product_id: string }>(
  acao: AcaoNoOndeFica<P> | undefined, peca: P,
): AcaoPronta | null {
  if (!acao) return null;
  return {
    rotulo: acao.rotulo,
    fazer: () => acao.fazer(peca),
    jaFeito: acao.jaFeito?.(peca.product_id) ?? null,
    notaDoMapa: acao.notaDoMapa,
  };
}

// --- A faixa do resultado ------------------------------------------------------

/**
 * A FAIXA do lugar, logo abaixo do cartão da peça. Botão IRMÃO do cartão (não
 * filho: botão dentro de botão é HTML inválido, a lição dos chips de
 * fornecedor), na largura toda e com 44 px ou mais — é o dedo de quem está em
 * pé no balcão. Antes o selo do lugar morava DENTRO do cartão, e tocá-lo punha
 * a peça no carrinho.
 *
 * Sem lugar (vazio ou "loja"): faixa tracejada que não abre nada — prometer um
 * mapa para "loja" seria mandar a pessoa procurar a loja inteira.
 */
export function FaixaDoLugar({ lugares, localBruto, aoAbrir, aoPorNumLugar, area }: {
  lugares: readonly LugarDaPeca[];
  /** Calota na área da marca: a faixa diz a área, não um gancho. */
  area?: AreaDaMarca | null;
  /** O que está gravado hoje, para dizer "hoje: loja" na faixa tracejada. */
  localBruto?: string | null;
  aoAbrir: () => void;
  /** Dono e gerente (fase 3): a faixa sem lugar ganha o "Pôr num lugar". Sem ele, só se lê. */
  aoPorNumLugar?: () => void;
}) {
  const cliente = useQueryClient();
  const tem = lugares.length > 0 || Boolean(area);
  // A planta do banco desce quando aparece o primeiro resultado COM lugar (uma
  // vez a cada 5 min, o mesmo cache da aba do mapa): quando a folha abrir, só
  // falta o desenho. O pacote do desenho, não — esse só desce no toque.
  useEffect(() => {
    if (!tem) return;
    void cliente.prefetchQuery({ queryKey: ['planta-da-loja'], queryFn: plantaDaLoja, staleTime: 5 * 60_000 });
  }, [tem, cliente]);

  if (area) {
    const onde = ondeFicaAArea(area);
    return (
      <button
        type="button"
        onClick={aoAbrir}
        data-faixa="area"
        aria-label={`Onde fica: área ${area.nome}, ${onde}`}
        className="flex min-h-[44px] w-full items-center gap-3 border-t border-fio px-4 py-2 text-left transition-colors hover:bg-paper/5"
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="shrink-0 rounded-md bg-edge px-2.5 py-1 text-sm font-bold text-paper">Área {area.nome}</span>
          <span className="text-sm text-mute">{onde}</span>
        </span>
        <span className="shrink-0 text-sm font-medium text-mute" aria-hidden="true">Ver ›</span>
      </button>
    );
  }

  if (!tem) {
    const hoje = (localBruto ?? '').trim();
    return (
      <div className="flex min-h-[44px] flex-wrap items-center gap-2 border-t border-fio px-4 py-2" data-faixa="sem-lugar">
        <span className="border border-dashed border-edge px-2.5 py-1 text-sm text-dim">
          sem lugar no mapa{hoje ? ` · hoje: ${hoje}` : ''}
        </span>
        {aoPorNumLugar && (
          <button type="button" onClick={aoPorNumLugar}
                  className="ml-auto min-h-[44px] px-2 text-sm font-medium text-signal-soft hover:text-paper">
            Pôr num lugar ›
          </button>
        )}
      </div>
    );
  }

  const [primeiro, ...outros] = lugares;
  const extenso = descreverEndereco(primeiro!.codigo);
  return (
    <button
      type="button"
      onClick={aoAbrir}
      data-faixa="lugar"
      aria-label={`Onde fica: ${primeiro!.codigo}${extenso ? `, ${extenso}` : ''}`}
      className="flex min-h-[44px] w-full items-center gap-3 border-t border-fio px-4 py-2 text-left transition-colors hover:bg-paper/5"
    >
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="numero shrink-0 rounded-md bg-edge px-2.5 py-1 text-sm text-paper">
            {primeiro!.codigo}
          </span>
          {extenso && <span className="text-sm text-mute">{extenso}</span>}
        </span>
        {outros.length > 0 && (
          <span className="mt-1 block text-xs text-dim">
            também em: {outros.map((l) => l.codigo).join(', ')}
          </span>
        )}
      </span>
      <span className="shrink-0 text-sm font-medium text-mute" aria-hidden="true">Ver ›</span>
    </button>
  );
}

// --- A folha ---------------------------------------------------------------------

/** O endereço do mapa grande para um lugar: ?local abre no lugar, ?zona é a reserva. */
export function linkDoMapaGrande(productId: string, codigo: string): string {
  const q = new URLSearchParams();
  const zona = zonaDoLocal(codigo);
  if (zona) q.set('zona', zona);
  q.set('local', codigo);
  q.set('peca', productId);
  return `/mapa?${q.toString()}`;
}

interface Props {
  aoFechar: () => void;
  /** Uma peça: abas por lugar, o código grande e o mini mapa. */
  peca?: PecaNaFolha;
  /** A aba que abre primeiro (o "Também em" da ficha abre no extra tocado). */
  abaInicial?: number;
  acao?: AcaoPronta | null;
  /** Abre direto no "Mudar de lugar" (o "Pôr num lugar" da faixa sem lugar). Só vale com as ações. */
  comecarMudando?: boolean;
  /** "Onde buscar tudo": as peças do carrinho, na ordem da caminhada. */
  lista?: PecaNaFolha[];
  /** A frase do que ficou de fora da lista ("2 peças diversas não têm lugar no mapa"). */
  semLugar?: string | null;
}

export default function OndeFica({ aoFechar, peca, abaInicial = 0, acao, comecarMudando = false, lista, semLugar }: Props) {
  const fechar = useRef(aoFechar);
  fechar.current = aoFechar;

  // Esc fecha, no documento (o mesmo motivo da GaleriaCheia: a folha não tem
  // foco de teclado ao abrir). E o teclado do iPhone desce: com o campo de
  // busca ainda focado, ele comeria metade da folha.
  useEffect(() => {
    const ativo = document.activeElement;
    if (ativo instanceof HTMLElement && (ativo.tagName === 'INPUT' || ativo.tagName === 'TEXTAREA')) ativo.blur();
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar.current(); };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, []);

  const titulo = lista ? 'Onde buscar tudo' : `Onde fica: ${peca?.descricao ?? ''}`;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/80 sm:items-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) aoFechar(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="flex h-[78svh] w-full max-w-2xl flex-col border-t border-edge bg-panel shadow-2xl sm:h-auto sm:max-h-[88svh] sm:border"
      >
        {lista
          ? <Lista pecas={lista} semLugar={semLugar ?? null} aoFechar={aoFechar} />
          : peca
            ? peca.area
              ? <CalotaNaArea peca={peca} area={peca.area} acao={acao ?? null} aoFechar={aoFechar} />
              : <UmaPeca peca={peca} abaInicial={abaInicial} acao={acao ?? null}
                         comecarMudando={comecarMudando} aoFechar={aoFechar} />
            : null}
      </div>
    </div>
  );
}

function BotaoFechar({ aoFechar }: { aoFechar: () => void }) {
  return (
    <button type="button" onClick={aoFechar}
            className="min-h-[44px] shrink-0 border border-edge px-4 text-sm font-bold hover:border-signal">
      Fechar
    </button>
  );
}

function UmaPeca({ peca, abaInicial, acao, comecarMudando, aoFechar }: {
  peca: PecaNaFolha;
  abaInicial: number;
  acao: AcaoPronta | null;
  comecarMudando: boolean;
  aoFechar: () => void;
}) {
  // Dono e gerente: o "Mudar de lugar" / "Pôr num lugar". Vendedor: nada.
  const podeMudar = usePodeMudarEndereco();
  const [aba, setAba] = useState(() => Math.min(Math.max(abaInicial, 0), Math.max(peca.lugares.length - 1, 0)));
  // Depois de uma mudança a peça pode ter um lugar a menos: a aba não aponta para o vazio.
  const lugar = peca.lugares[Math.min(aba, peca.lugares.length - 1)] ?? null;
  // O lugar que sai: o da aba (um extra move só ele); null = sem lugar ("Pôr num lugar").
  const [mudando, setMudando] = useState<{ saiDe: string | null } | null>(
    () => (comecarMudando && podeMudar ? { saiDe: lugar?.codigo ?? null } : null));
  const extenso = lugar ? descreverEndereco(lugar.codigo) : null;
  const codigos = peca.lugares.map((l) => l.codigo);

  return (
    <>
      <CabecalhoDaPeca peca={peca} aoFechar={aoFechar} />

      {mudando && podeMudar ? (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
          <Suspense fallback={<Carregando texto="Abrindo o mudar de lugar" />}>
            <MudarNaFolha saiDe={mudando.saiDe} aoVoltar={() => setMudando(null)}
                          peca={{ product_id: peca.product_id, descricao: peca.descricao, sku: peca.sku }} />
          </Suspense>
        </div>
      ) : (
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4">
        {peca.lugares.length > 1 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Lugares da peça">
            {peca.lugares.map((l, i) => (
              <button key={l.codigo} type="button" aria-pressed={aba === i} onClick={() => setAba(i)}
                      className={[
                        'min-h-[44px] border px-3 text-left text-sm',
                        aba === i ? 'border-signal bg-signal/15 text-paper' : 'border-edge text-mute',
                      ].join(' ')}>
                <span className="text-xs">{l.extra ? 'Também' : 'Principal'} · </span>
                <span className="font-mono font-bold">{l.codigo}</span>
              </button>
            ))}
          </div>
        )}

        {lugar ? (
          <>
            <div>
              <p className="inline-block rounded-lg bg-edge px-3 py-1.5 font-mono text-2xl font-bold text-paper">
                {lugar.codigo}
              </p>
              {extenso && <p className="mt-2 text-base text-mute">{extenso}</p>}
            </div>
            <Suspense fallback={<Carregando texto="Abrindo o mapa" />}>
              <MiniMapa codigos={codigos} foco={lugar.codigo}
                        aoEscolher={(c) => { const i = codigos.indexOf(c); if (i >= 0) setAba(i); }} />
            </Suspense>
          </>
        ) : (
          <p className="border border-dashed border-edge p-3 text-sm text-dim">Esta peça não tem lugar no mapa.</p>
        )}

        <div className="space-y-2 pt-1">
          {acao && (acao.jaFeito
            ? <p className="text-sm text-ok">{acao.jaFeito}</p>
            : (
              <button type="button" className="botao-primario min-h-[44px] w-full"
                      onClick={() => { acao.fazer(); aoFechar(); }}>
                {acao.rotulo}
              </button>
            ))}
          {podeMudar && (
            <button type="button" className="botao-secundario min-h-[44px] w-full"
                    onClick={() => setMudando({ saiDe: lugar?.codigo ?? null })}>
              {lugar ? 'Mudar de lugar' : 'Pôr num lugar'}
            </button>
          )}
          {lugar && (
            <>
              <Link to={linkDoMapaGrande(peca.product_id, lugar.codigo)}
                    className="botao-secundario flex min-h-[44px] w-full items-center justify-center">
                Abrir no mapa grande
              </Link>
              {acao?.notaDoMapa && <p className="text-center text-xs text-dim">{acao.notaDoMapa}</p>}
            </>
          )}
        </div>
      </div>
      )}
    </>
  );
}

function CabecalhoDaPeca({ peca, aoFechar }: { peca: PecaNaFolha; aoFechar: () => void }) {
  return (
    <div className="flex items-start gap-3 border-b border-edge p-4">
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[0.6875rem] uppercase tracking-wider text-dim">
          {peca.sku}{peca.marca ? ` · ${peca.marca}` : ''}
        </p>
        <p className="font-bold leading-snug">{peca.descricao}</p>
        {/* Quanto HÁ, nunca quanto se vendeu: faturamento é só do dono. */}
        {peca.quantidade !== undefined && peca.quantidade !== null && (
          <p className={`font-mono text-xs ${peca.quantidade > 0 ? 'text-ok' : 'text-erro'}`}>
            {peca.quantidade > 0 ? `${qtd(peca.quantidade)} em estoque` : 'sem estoque'}
          </p>
        )}
      </div>
      <BotaoFechar aoFechar={aoFechar} />
    </div>
  );
}

/**
 * A calota na ÁREA DA MARCA (15/10/2026): a folha diz a área ("Área Ford ·
 * parede da entrada") e mostra a parede de frente com os ganchos da marca
 * cheios. Não há "Mudar de lugar": o lugar da calota é a marca — quem muda a
 * área é o mapa (a marca de cada gancho, no editor).
 */
function CalotaNaArea({ peca, area, acao, aoFechar }: {
  peca: PecaNaFolha;
  area: AreaDaMarca;
  acao: AcaoPronta | null;
  aoFechar: () => void;
}) {
  const [i, setI] = useState(0);
  const parede = area.paredes[Math.min(i, area.paredes.length - 1)]!;
  // O gancho gravado fica velho (é por isso que existe a área); a pilha de reserva gravada, não.
  const reserva = peca.lugares.filter((l) => !/^GC-/i.test(l.codigo));
  return (
    <>
      <CabecalhoDaPeca peca={peca} aoFechar={aoFechar} />
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4">
        <div>
          <p className="inline-block rounded-lg bg-edge px-3 py-1.5 text-2xl font-bold text-paper">Área {area.nome}</p>
          <p className="mt-2 text-base text-mute">{ondeFicaAArea(area)}</p>
          <p className="mt-1 text-sm text-dim">
            A calota fica num desses ganchos, junto das outras {daMarca(area)}.
          </p>
          {reserva.length > 0 && (
            <p className="mt-1 text-sm text-mute">
              Reserva também em: {reserva.map((l) => descreverEndereco(l.codigo) ?? l.codigo).join('; ')}
            </p>
          )}
        </div>

        {area.paredes.length > 1 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Paredes da área">
            {area.paredes.map((p, n) => (
              <button key={p.nome} type="button" aria-pressed={n === i} onClick={() => setI(n)}
                      className={[
                        'min-h-[44px] border px-3 text-left text-sm',
                        n === i ? 'border-signal bg-signal/15 text-paper' : 'border-edge text-mute',
                      ].join(' ')}>
                {p.nome} <span className="text-xs">· {p.ganchos.length} ganchos</span>
              </button>
            ))}
          </div>
        )}

        <Suspense fallback={<Carregando texto="Abrindo o mapa" />}>
          <MiniMapa key={parede.nome} codigos={parede.ganchos} foco={parede.ganchos[0] ?? null} area={area} />
        </Suspense>

        <div className="space-y-2 pt-1">
          {acao && (acao.jaFeito
            ? <p className="text-sm text-ok">{acao.jaFeito}</p>
            : (
              <button type="button" className="botao-primario min-h-[44px] w-full"
                      onClick={() => { acao.fazer(); aoFechar(); }}>
                {acao.rotulo}
              </button>
            ))}
          <Link to={linkDoMapaGrande(peca.product_id, parede.ganchos[0]!)}
                className="botao-secundario flex min-h-[44px] w-full items-center justify-center">
            Abrir no mapa grande
          </Link>
          {acao?.notaDoMapa && <p className="text-center text-xs text-dim">{acao.notaDoMapa}</p>}
        </div>
      </div>
    </>
  );
}

/** "Onde buscar tudo": uma linha por peça, na ordem da caminhada; tocar foca o lugar no desenho. */
function Lista({ pecas, semLugar, aoFechar }: {
  pecas: readonly PecaNaFolha[];
  semLugar: string | null;
  aoFechar: () => void;
}) {
  const linhas = ordemDeCaminhada(
    pecas.filter((p) => p.area || p.lugares.length > 0)
      .map((p) => ({ peca: p, codigo: p.area ? ganchosDaArea(p.area)[0]! : p.lugares[0]!.codigo })),
  );
  // O foco é a PEÇA: duas calotas da mesma marca começam no mesmo gancho.
  const [foco, setFoco] = useState<string | null>(null);
  const focada = linhas.find((l) => l.peca.product_id === foco) ?? null;
  // Todos os lugares de todas as peças acendem — o extra também é caminho. A
  // calota com área entra por UM gancho (17 alfinetes da mesma marca seriam
  // ruído); focada, a parede mostra a área inteira.
  const codigos = focada?.peca.area
    ? ganchosDaArea(focada.peca.area)
    : [...new Set(linhas.flatMap((l) => (l.peca.area ? [l.codigo] : l.peca.lugares.map((x) => x.codigo))))];
  // Tocar num alfinete: a peça daquele lugar — o principal, um extra ou um gancho da área.
  const pecaDoLugar = (c: string) => linhas.find((l) => l.codigo === c
    || l.peca.lugares.some((x) => x.codigo === c)
    || (l.peca.area ? ganchosDaArea(l.peca.area).includes(c) : false))?.peca.product_id ?? null;

  return (
    <>
      <div className="flex items-start gap-3 border-b border-edge p-4">
        <div className="min-w-0 flex-1">
          <p className="font-bold">Onde buscar tudo</p>
          <p className="text-xs text-dim">
            {linhas.length} {linhas.length === 1 ? 'peça' : 'peças'}, na ordem da caminhada: frente, corredores, fundos.
          </p>
        </div>
        <BotaoFechar aoFechar={aoFechar} />
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4">
        <Suspense fallback={<Carregando texto="Abrindo o mapa" />}>
          <MiniMapa key={focada?.peca.area ? `area-${focada.peca.product_id}` : 'lista'}
                    codigos={codigos} foco={focada?.codigo ?? null} area={focada?.peca.area ?? null}
                    aoEscolher={(c) => setFoco(pecaDoLugar(c))}
                    daMesmaPeca={focada && !focada.peca.area ? focada.peca.lugares.map((x) => x.codigo) : undefined} />
        </Suspense>
        <ol className="divide-y divide-edge border-y border-edge">
          {linhas.map(({ peca, codigo }, n) => {
            const ativo = foco === peca.product_id;
            return (
              <li key={peca.product_id}>
                <button type="button" aria-pressed={ativo}
                        onClick={() => setFoco(ativo ? null : peca.product_id)}
                        className={`flex min-h-[44px] w-full items-center gap-3 px-2 py-2 text-left ${ativo ? 'bg-signal/10' : ''}`}>
                  <span className="w-5 shrink-0 text-right font-mono text-xs text-mute">{n + 1}</span>
                  <span className={`shrink-0 rounded-md bg-edge px-2 py-0.5 text-sm font-bold text-paper ${peca.area ? '' : 'font-mono'}`}>
                    {peca.area ? `Área ${peca.area.nome}` : codigo}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{peca.descricao}</span>
                    {peca.area && <span className="block text-xs text-mute">{ondeFicaAArea(peca.area)}</span>}
                    {!peca.area && peca.lugares.length > 1 && (
                      <span className="block text-xs text-mute">
                        também em: {peca.lugares.slice(1).map((l) => l.codigo).join(', ')}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        {semLugar && <p className="text-xs text-dim">{semLugar}</p>}
      </div>
    </>
  );
}
